import { useEffect, useMemo, useRef, useState } from 'react';
import type { Drone, DroneTelemetry } from '@shared/types';
import { telemetryBus } from './bus';
import { acquireLink, useFleetLinkStore, type FleetLinkState } from './store';
import { clearTrail } from './trail';

function selectEntry(droneId: string | undefined) {
  return (state: { entries: Record<string, FleetLinkState> }): FleetLinkState | undefined =>
    droneId === undefined ? undefined : state.entries[droneId];
}

/** 订阅单台无人机的链路状态；drone 变化或卸载时自动断开。 */
export function useDroneLink(drone: Drone | null | undefined): FleetLinkState | undefined {
  const droneId = drone?.id;
  const entry = useFleetLinkStore(useMemo(() => selectEntry(droneId), [droneId]));

  useEffect(() => {
    if (!drone) return;
    return acquireLink(drone);
  }, [drone]);

  return entry;
}

/**
 * 批量订阅机队链路（总览页使用）。
 * `enabled` 为 false 的无人机不会建立连接；禁用设备（drone.enabled === false）同样跳过。
 */
export function useFleetLinks(drones: Drone[], options?: { enabled?: boolean }): Record<string, FleetLinkState> {
  const enabled = options?.enabled ?? true;
  const entries = useFleetLinkStore((state) => state.entries);
  const dronesRef = useRef(drones);
  dronesRef.current = drones;

  // drones 数组每次拉取都会换身份，因此用「id + updatedAt」拼出的稳定键来驱动订阅
  const subscriptionKey = drones
    .filter((drone) => drone.enabled)
    .map((drone) => `${drone.id}:${drone.updatedAt}`)
    .join('|');

  useEffect(() => {
    if (!enabled) return;
    const releases = dronesRef.current.filter((drone) => drone.enabled).map((drone) => acquireLink(drone));
    return () => {
      for (const release of releases) release();
    };
  }, [enabled, subscriptionKey]);

  return entries;
}

/**
 * 页面级航迹生命周期。
 *
 * 尾迹只在「这个界面打开期间」有效：一旦离开该页面就清空，
 * 再次进入时从当前机位重新开始画，不会带着上一次的残留轨迹。
 *
 * 为什么不能只靠 `acquireLink` 的引用计数来清理：
 * 机队总览、无人机管理、单机管理都订阅同一批链路，页面之间跳转时
 * 引用计数并不会归零，尾迹就会跨页面残留。
 * 因此这里在**每次挂载**时主动清空一次，并在卸载时再清一次。
 */
export function useTrailSession(): void {
  useEffect(() => {
    clearTrail();
    return () => clearTrail();
  }, []);
}

/** 以固定节奏（默认 1 秒）触发重渲染，用于渲染「最后更新于 N 秒前」这类相对时间。 */
export function useLiveTick(intervalMs = 1_000): number {
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setTick(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return tick;
}

/** 直接订阅每一帧遥测（不触发重渲染），用于姿态球、大数字等硬实时视图。 */
export function useTelemetryFrames(
  droneId: string | undefined,
  listener: (telemetry: DroneTelemetry) => void,
): void {
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  useEffect(() => {
    if (!droneId) return;
    return telemetryBus.on(droneId, (telemetry) => listenerRef.current(telemetry));
  }, [droneId]);
}

/** 以 rAF 合帧的方式消费遥测：高频帧只在下一帧绘制一次。 */
export function useTelemetryAnimationFrame(
  droneId: string | undefined,
  listener: (telemetry: DroneTelemetry) => void,
): void {
  const listenerRef = useRef(listener);
  listenerRef.current = listener;
  const latest = useRef<DroneTelemetry | null>(null);

  useEffect(() => {
    if (!droneId) return;
    let frame = 0;
    const pump = () => {
      frame = 0;
      const telemetry = latest.current;
      if (telemetry) listenerRef.current(telemetry);
    };
    const unsubscribe = telemetryBus.on(droneId, (telemetry) => {
      latest.current = telemetry;
      if (frame === 0) frame = window.requestAnimationFrame(pump);
    });
    return () => {
      unsubscribe();
      if (frame !== 0) window.cancelAnimationFrame(frame);
      latest.current = null;
    };
  }, [droneId]);
}
